/**
 * Team-roster API client (ADR-0145 Slice 2c).
 *
 * Per-feature client module (the `/v1/users` surface is not in the closed
 * `SillApi` bag). Each function returns a discriminated-union result so
 * the view can surface the load-bearing server reasons
 * (`last_owner_protected`, `self_*_change_refused`, `user_exists`, …) as
 * distinct toasts. 429 throws `AuthRateLimitError` (not `ApiError`) — it
 * is caught separately.
 */

import { AuthRateLimitError } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export type UserRoleWire = 'owner' | 'admin' | 'reviewer' | 'viewer';
export type UserStatusWire = 'invited' | 'active' | 'suspended' | 'deactivated';

export interface UserWire {
  user_id: string;
  email: string;
  name: string;
  role: UserRoleWire;
  status: UserStatusWire;
  created_at: string;
  last_active_at: string | null;
  invited_by?: string;
  // ADR-0147 Slice 2 — per-member access summary (list only).
  site_access?: { mode: 'all' | 'selected'; site_count: number };
}

/** ADR-0147 — the access-scope wire shape (set + read). */
export type SiteAccessInput =
  | { mode: 'all' }
  | { mode: 'selected'; site_ids: string[] };

export type ListUsersResult =
  | { kind: 'ok'; users: readonly UserWire[] }
  | { kind: 'error'; message: string };

export async function listUsers(http: HttpClient): Promise<ListUsersResult> {
  try {
    const res = await http.request<{ users?: unknown }>({
      method: 'GET',
      path: '/v1/users',
    });
    if (res === undefined || !Array.isArray(res.users)) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', users: res.users as UserWire[] };
  } catch (err) {
    return { kind: 'error', message: explain(err) };
  }
}

export type InviteResult =
  | { kind: 'ok'; user: UserWire }
  | { kind: 'conflict' } // 409 user_exists
  | { kind: 'forbidden' } // 403 role_insufficient
  | { kind: 'bad_request'; reason?: string }
  | { kind: 'rate_limited' } // 429 (per-account daily cap or magic-link limiter)
  | { kind: 'error'; message: string };

export async function inviteUser(
  http: HttpClient,
  body: {
    email: string;
    name?: string;
    role: UserRoleWire;
    site_access?: SiteAccessInput;
    /** ADR-0168 — install-handoff invite: the email names the site's
     *  domain and points the invitee at the setup guide. */
    invite_context?: { kind: 'install'; site_id: string };
  },
): Promise<InviteResult> {
  try {
    const res = await http.request<{ user?: UserWire }>({
      method: 'POST',
      path: '/v1/users',
      body,
    });
    if (res === undefined || res.user === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', user: res.user };
  } catch (err) {
    if (err instanceof AuthRateLimitError) return { kind: 'rate_limited' };
    if (err instanceof ApiError) {
      if (err.kind === 'conflict') return { kind: 'conflict' };
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'bad_request') {
        return { kind: 'bad_request', ...reasonOf(err.server_reason) };
      }
    }
    return { kind: 'error', message: explain(err) };
  }
}

export type UpdateUserResult =
  | { kind: 'ok'; user: UserWire }
  | { kind: 'forbidden' } // 403 role_insufficient (incl. admin touching an owner)
  | { kind: 'conflict'; reason?: string } // 409 last_owner_protected / self_*_change_refused
  | { kind: 'not_found' }
  | { kind: 'bad_request'; reason?: string }
  | { kind: 'error'; message: string };

export async function updateUser(
  http: HttpClient,
  user_id: string,
  body: { role?: UserRoleWire; status?: UserStatusWire },
): Promise<UpdateUserResult> {
  try {
    const res = await http.request<{ user?: UserWire }>({
      method: 'PATCH',
      path: `/v1/users/${encodeURIComponent(user_id)}`,
      body,
    });
    if (res === undefined || res.user === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return { kind: 'ok', user: res.user };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'conflict') {
        return { kind: 'conflict', ...reasonOf(err.server_reason) };
      }
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'bad_request') {
        return { kind: 'bad_request', ...reasonOf(err.server_reason) };
      }
    }
    return { kind: 'error', message: explain(err) };
  }
}

export type ResendInviteResult =
  | { kind: 'ok' } // 204
  | { kind: 'forbidden' }
  | { kind: 'conflict'; reason?: string } // 409 not_invited
  | { kind: 'not_found' }
  | { kind: 'rate_limited' }
  | { kind: 'error'; message: string };

/** ADR-0152 Slice C — remote sign-out (force-revoke a member's sessions). */
export type RevokeSessionsResult =
  | { kind: 'ok'; revoked_count: number }
  | { kind: 'forbidden'; reason?: string } // role_insufficient | owner_protected
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

/** ADR-0153 §4 — guarded hard-delete of a never-active member (typo invite). */
export type DeleteMemberResult =
  | { kind: 'ok' } // 204
  | { kind: 'forbidden' }
  | { kind: 'not_found' }
  | { kind: 'has_history' } // 409 — never-active gate failed; use Deactivate
  | { kind: 'error'; message: string };

export async function deleteMember(
  http: HttpClient,
  user_id: string,
): Promise<DeleteMemberResult> {
  try {
    await http.request<undefined>({
      method: 'DELETE',
      path: `/v1/users/${encodeURIComponent(user_id)}`,
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'conflict' && err.server_reason === 'has_history') {
        return { kind: 'has_history' };
      }
    }
    return { kind: 'error', message: explain(err) };
  }
}

export async function revokeMemberSessions(
  http: HttpClient,
  user_id: string,
): Promise<RevokeSessionsResult> {
  try {
    const res = await http.request<{ revoked_count?: number }>({
      method: 'POST',
      path: `/v1/users/${encodeURIComponent(user_id)}/revoke-sessions`,
      body: {},
    });
    return { kind: 'ok', revoked_count: res?.revoked_count ?? 0 };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden', ...reasonOf(err.server_reason) };
      if (err.kind === 'not_found') return { kind: 'not_found' };
    }
    return { kind: 'error', message: explain(err) };
  }
}

export async function resendInvite(
  http: HttpClient,
  user_id: string,
): Promise<ResendInviteResult> {
  try {
    await http.request<undefined>({
      method: 'POST',
      path: `/v1/users/${encodeURIComponent(user_id)}/resend-invite`,
      body: {},
    });
    return { kind: 'ok' };
  } catch (err) {
    if (err instanceof AuthRateLimitError) return { kind: 'rate_limited' };
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'conflict') {
        return { kind: 'conflict', ...reasonOf(err.server_reason) };
      }
      if (err.kind === 'not_found') return { kind: 'not_found' };
    }
    return { kind: 'error', message: explain(err) };
  }
}

/** A site as the access picker needs it (ADR-0147 Slice 2). */
export interface PickerSite {
  site_id: string;
  name: string;
  domain: string;
}

export type ListAccountSitesResult =
  | { kind: 'ok'; sites: readonly PickerSite[] }
  | { kind: 'error'; message: string };

/**
 * The account's sites for the access picker. Reads the first page of
 * GET /v1/sites (`{ items }`); pagination beyond the first page is a
 * follow-up for very large (agency) accounts — flagged in ADR-0147 §9.
 */
export async function listAccountSites(
  http: HttpClient,
): Promise<ListAccountSitesResult> {
  try {
    const res = await http.request<{ items?: unknown }>({
      method: 'GET',
      path: '/v1/sites',
    });
    if (res === undefined || !Array.isArray(res.items)) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const sites = (res.items as Record<string, unknown>[]).map((s) => ({
      site_id: String(s.site_id ?? ''),
      name: typeof s.name === 'string' ? s.name : '',
      domain: typeof s.domain === 'string' ? s.domain : '',
    }));
    return { kind: 'ok', sites };
  } catch (err) {
    return { kind: 'error', message: explain(err) };
  }
}

export type GetSiteAccessResult =
  | { kind: 'ok'; mode: 'all' | 'selected'; site_ids: readonly string[] }
  | { kind: 'forbidden' } // 403 role_insufficient
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

export async function getSiteAccess(
  http: HttpClient,
  user_id: string,
): Promise<GetSiteAccessResult> {
  try {
    const res = await http.request<{ mode?: string; site_ids?: unknown }>({
      method: 'GET',
      path: `/v1/users/${encodeURIComponent(user_id)}/site-access`,
    });
    if (
      res === undefined ||
      (res.mode !== 'all' && res.mode !== 'selected') ||
      !Array.isArray(res.site_ids)
    ) {
      return { kind: 'error', message: 'Server returned an unexpected response.' };
    }
    return { kind: 'ok', mode: res.mode, site_ids: res.site_ids as string[] };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
    }
    return { kind: 'error', message: explain(err) };
  }
}

export type SetSiteAccessResult =
  | { kind: 'ok'; mode: 'all' | 'selected'; site_ids: readonly string[] }
  | { kind: 'forbidden' } // 403 role_insufficient
  | { kind: 'conflict' } // 409 self_access_change_refused
  | { kind: 'not_found' } // 404 user_not_found
  // 400: account_wide_role_user | empty_site_selection | invalid_site
  | { kind: 'bad_request'; reason?: string }
  | { kind: 'error'; message: string };

export async function setSiteAccess(
  http: HttpClient,
  user_id: string,
  body: SiteAccessInput,
): Promise<SetSiteAccessResult> {
  try {
    const res = await http.request<{ mode?: string; site_ids?: unknown }>({
      method: 'PUT',
      path: `/v1/users/${encodeURIComponent(user_id)}/site-access`,
      body,
    });
    if (
      res === undefined ||
      (res.mode !== 'all' && res.mode !== 'selected') ||
      !Array.isArray(res.site_ids)
    ) {
      return { kind: 'error', message: 'Server returned an unexpected response.' };
    }
    return { kind: 'ok', mode: res.mode, site_ids: res.site_ids as string[] };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'conflict') return { kind: 'conflict' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
      if (err.kind === 'bad_request') {
        return { kind: 'bad_request', ...reasonOf(err.server_reason) };
      }
    }
    return { kind: 'error', message: explain(err) };
  }
}

/** Parsed device descriptor (ADR-0150). */
export interface ParsedDevice {
  browser: string;
  os: string;
  device_type: string;
}

/** ADR-0149/0150 — one row of a member's activity trail. */
export interface ActivityEntry {
  log_id: string;
  action: string;
  context: Record<string, unknown>;
  created_at: string;
  // ADR-0150 — request origin (null for historical / non-browser writes).
  actor_ip: string | null;
  device: ParsedDevice | null;
  // ADR-0150 Slice 2 — geo from the IP (null when unavailable / private IP).
  // region = subdivision/state ISO code (e.g. "SC"); null for country-only IPs.
  location: { city: string | null; region: string | null; country: string | null } | null;
  /** ADR-0216 — the change was made by Sill support during a support
   *  session (not by this member). */
  via_sill_support?: boolean;
}

export type GetUserActivityResult =
  | { kind: 'ok'; items: readonly ActivityEntry[]; next_cursor: string | null }
  | { kind: 'forbidden' } // 403 role_insufficient
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

export async function getUserActivity(
  http: HttpClient,
  user_id: string,
  cursor?: string,
): Promise<GetUserActivityResult> {
  try {
    const q = cursor !== undefined ? `?cursor=${encodeURIComponent(cursor)}` : '';
    const res = await http.request<{ items?: unknown; next_cursor?: unknown }>({
      method: 'GET',
      path: `/v1/users/${encodeURIComponent(user_id)}/activity${q}`,
    });
    if (res === undefined || !Array.isArray(res.items)) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    return {
      kind: 'ok',
      items: res.items as ActivityEntry[],
      next_cursor: typeof res.next_cursor === 'string' ? res.next_cursor : null,
    };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'not_found') return { kind: 'not_found' };
    }
    return { kind: 'error', message: explain(err) };
  }
}

/** Build the optional `reason` field without violating exactOptionalPropertyTypes. */
function reasonOf(reason: string | undefined): { reason?: string } {
  return reason === undefined ? {} : { reason };
}

function explain(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.kind) {
      case 'service_unavailable':
        return 'Team service is temporarily unavailable.';
      case 'network_timeout':
        return 'This may have saved — refresh to check before trying again.';
      case 'network_error':
        return 'Network error — check your connection.';
      case 'bad_response':
        return 'Server returned an unexpected response.';
      default:
        return err.message;
    }
  }
  return err instanceof Error ? err.message : 'Network error.';
}
